import { z } from "zod";
import { Agent } from "../agent";
import { makeEvaluator } from "../evaluator";
import { Tool } from "../tool";

type AgentToolEvaluatorProps = {
  agent: Agent;
  toolName: string;
  description: string;
  promptTemplate?: string;
  parameters?: z.ZodTypeAny;
  followUpMessage?: string;
};

/**
 * Creates an evaluator that allows one agent to use another agent as a tool.
 * This is useful for delegating specific tasks to specialized agents.
 * 
 * @param props Configuration for the agent tool evaluator
 * @returns An evaluator function that integrates an agent as a tool
 */
export const agentToolEvaluator = ({
  agent,
  toolName,
  description,
  promptTemplate = "{{input}}",
  parameters,
  followUpMessage,
}: AgentToolEvaluatorProps) =>
  makeEvaluator(({ thread, complete }) => {
    // Create a tool that wraps the agent
    const agentTool = new Tool({
      name: toolName,
      description: description,
      parameters: parameters || z.object({
        input: z.string().describe("The input to send to the agent"),
      }),
      followUpMessage,
      executeFn: async (args, _toolCall) => {
        // Format the input using the template if needed
        let prompt = "";
        
        if ('input' in args && typeof args.input === 'string') {
          // Simple string input
          prompt = promptTemplate.replace("{{input}}", args.input);
        } else {
          // For more complex parameter schemas, serialize args to JSON and include in prompt
          prompt = promptTemplate.replace("{{input}}", JSON.stringify(args, null, 2));
        }
        
        // Create a new thread with the specialized agent
        const agentThread = agent.createThread({
          prompt,
        });
        
        try {
          // Send the thread and wait for the response
          const response = await agentThread.send();
          
          // Get the last message from the thread
          const lastMessage = response.last();
          
          // Add this agent thread to the parent agent's memory
          agent.closeThread(agentThread.id);
          
          // Return the response content
          return lastMessage?.content || "No response from agent";
        } catch (error) {
          console.error(`Error executing agent tool ${toolName}:`, error);
          return `Error executing agent: ${error instanceof Error ? error.message : String(error)}`;
        }
      },
    });
    
    // Add the agent tool to the thread
    thread.addTool(agentTool);
    
    // No immediate action needed, just set up the tool
    // The evaluator will be complete when the parent thread is done
    complete(true);
    
    // No cleanup needed
    return undefined;
  });